import React, { useState } from 'react';
import isEmpty from 'lodash/isEmpty';
import { Box, Button, Text } from '@sparknz/spark-pattern-library';
import { LinkRegion } from '@sparknz/tracking';

import { EligibilityAccounts } from 'journey/productDetails/typings';
import { ChoiceBoxSelector, DropDownSelector } from 'shared/components';
import { useWearableDevice } from '../../../../hooks';

interface EligibilityAccountSelectorProps {
  accounts: EligibilityAccounts;
  onAccountSelection: (accountNumber: string | null) => void;
  handleNoAccounts: () => void;
}

const MAX_ACCOUNTS_FOR_CHOICE_BOX = 5;
const PREPAID_ACCOUNT = 'prepaid-account';

const EligibilityAccountSelector = ({
  accounts,
  onAccountSelection,
  handleNoAccounts,
}: EligibilityAccountSelectorProps) => {
  const [selectedAccountNumber, setSelectedAccountNumber] = useState<
    string | null
  >(null);
  const { isBrandMatch: isSamsungWearable } = useWearableDevice('Samsung');

  const useDropdownSelector = accounts.length > MAX_ACCOUNTS_FOR_CHOICE_BOX;

  const skipAccountSelection = accounts.length === 1 && !isSamsungWearable;

  if (isEmpty(accounts)) {
    onAccountSelection(null);
    handleNoAccounts();
    return null;
  }

  if (skipAccountSelection) {
    onAccountSelection(accounts[0].accountNumber);
    return null;
  }

  const isSelectedAccountIneligible = accounts.some(
    ({ accountNumber, isEligible }) =>
      (accountNumber || PREPAID_ACCOUNT) === selectedAccountNumber &&
      !isEligible,
  );

  const isContinueCTADisabled =
    !selectedAccountNumber ||
    (isSelectedAccountIneligible && isSamsungWearable);

  return (
    <Box pt="s5">
      <Text variant="subheadingM">Select your account</Text>
      <LinkRegion isNotTracked>
        {useDropdownSelector ? (
          <DropDownSelector
            accountList={[...accounts]}
            isSamsungWearable={isSamsungWearable}
            selectedAccountNumber={selectedAccountNumber}
            setSelectedAccountNumber={setSelectedAccountNumber}
          />
        ) : (
          <ChoiceBoxSelector
            accountList={[...accounts]}
            isSamsungWearable={isSamsungWearable}
            selectedAccountNumber={selectedAccountNumber}
            setSelectedAccountNumber={setSelectedAccountNumber}
          />
        )}
      </LinkRegion>
      <LinkRegion regionId="productDetails.account.complete">
        <Button
          variant="primary"
          onClick={() => {
            onAccountSelection(selectedAccountNumber);
          }}
          disabled={isContinueCTADisabled}
          fullWidth
        >
          Continue
        </Button>
      </LinkRegion>
    </Box>
  );
};

export default EligibilityAccountSelector;
